import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData } from '@tanstack/react-query';
import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { AlertCircle, Inbox, RefreshCw, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAtomValue } from 'jotai';

import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { DataTableSkeleton } from '@/components/ui/data-table-skeleton';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SupportPriorityBadge, SupportStatusBadge } from '@/components/support/SupportBadges';
import { supportCategoryLabel } from '@/components/support/labels';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useSupportSummary, useSupportTickets, useUpdateSupportTicket } from '@/hooks/useSupport';
import { useEmployees } from '@/lib/hooks/useEmployees';
import { authStateAtom } from '@/stores/auth';
import { EmployeeRole } from '@repo/types';
import type { AdminSupportTicket, AdminSupportTicketFilters } from '@repo/types';

export const Route = createFileRoute('/admin/support/')({
  component: SupportQueuePage,
});

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active (open + in progress)' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'waiting_on_customer', label: 'Waiting on customer' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'All statuses' },
];

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'Any priority' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

/** Roles the API lets take a ticket off another staff member. */
const MANAGE_ROLES: string[] = [EmployeeRole.Manager, EmployeeRole.Admin];

/** Radix Select needs a real value for "nobody"; mapped back to null on save. */
const UNASSIGNED = 'unassigned';

const ASSIGNEE_OPTIONS = [
  { value: 'all', label: 'Anyone' },
  { value: 'me', label: 'Assigned to me' },
  { value: 'unassigned', label: 'Unassigned' },
];

function relative(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : formatDistanceToNow(date, { addSuffix: true });
}

/** One counter tile in the queue header. */
function Stat({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-semibold tabular-nums ${tone ?? ''}`}>{value}</p>
    </div>
  );
}

/**
 * Assignment control, rendered per row so each carries its own mutation state.
 *
 * Triage happens here rather than one ticket at a time, so this offers the whole
 * team, not just "claim it myself" — with the same gate the API applies: taking
 * a ticket off someone else needs manage rights.
 */
function AssigneeCell({
  ticket,
  userId,
  canManage,
  employees,
}: {
  ticket: AdminSupportTicket;
  userId?: string;
  canManage: boolean;
  employees: { id: string; name: string }[];
}) {
  const update = useUpdateSupportTicket(ticket.id);
  const canReassign = canManage || ticket.assignedTo === null || ticket.assignedTo === userId;

  // A deactivated assignee is absent from the active list, and a Select with no
  // matching item silently renders as unassigned. Keep them as an option.
  const options =
    ticket.assignedTo && !employees.some((e) => e.id === ticket.assignedTo)
      ? [{ id: ticket.assignedTo, name: ticket.assignedToName ?? 'Unknown' }, ...employees]
      : employees;

  if (!canReassign) {
    return (
      <span className="text-sm text-muted-foreground">{ticket.assignedToName ?? 'Assigned'}</span>
    );
  }

  return (
    // The row's title is a link; stop clicks here from also navigating.
    <div onClick={(event) => event.stopPropagation()}>
      <Select
        value={ticket.assignedTo ?? UNASSIGNED}
        disabled={update.isPending}
        onValueChange={(value) =>
          update.mutate({ assignedTo: value === UNASSIGNED ? null : value })
        }
      >
        <SelectTrigger className="h-8 w-[150px] text-xs" aria-label="Assignee">
          <SelectValue placeholder="Unassigned" />
        </SelectTrigger>
        {/* Bounded height: the shared content fills the viewport, which a whole
            team of assignees turns into a wall over the page. */}
        <SelectContent className="max-h-64">
          <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
          {options.map((employee) => (
            <SelectItem key={employee.id} value={employee.id}>
              {employee.name}
              {employee.id === userId ? ' (me)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SupportQueuePage() {
  const navigate = useNavigate();
  // `employees.id` IS the JWT subject for a staff session, so this is the value
  // the API compares `assigned_to` against.
  const authUser = useAtomValue(authStateAtom).user;
  const userId = authUser?.id;
  const canManage = MANAGE_ROLES.includes(authUser?.role ?? '');

  // Every staff role can work support, so every active employee is assignable.
  const { data: employeePage } = useEmployees({ status: 'active' }, 1, 100);
  const assignees = useMemo(() => {
    const all = [...(employeePage?.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
    const mine = all.filter((e) => e.id === userId);
    return [...mine, ...all.filter((e) => e.id !== userId)];
  }, [employeePage, userId]);

  const openTicket = (ticketId: string) =>
    navigate({ to: '/admin/support/$ticketId', params: { ticketId } });

  const [search, setSearch] = useState('');
  // Opens on the work that is actually outstanding — a queue that defaults to
  // "all" buries today's tickets under every ticket ever closed.
  const [status, setStatus] = useState('active');
  const [priority, setPriority] = useState('all');
  const [assignee, setAssignee] = useState('all');
  const [page, setPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const debouncedSearch = useDebouncedValue(search, 300);

  const filters = useMemo<AdminSupportTicketFilters>(
    () => ({
      status: status as AdminSupportTicketFilters['status'],
      priority:
        priority !== 'all' ? (priority as AdminSupportTicketFilters['priority']) : undefined,
      assignedTo: assignee !== 'all' ? assignee : undefined,
      q: debouncedSearch || undefined,
      page,
      limit: 25,
    }),
    [status, priority, assignee, debouncedSearch, page]
  );

  const { data, isLoading, isFetching, error, refetch } = useSupportTickets(filters, {
    placeholderData: keepPreviousData,
  });

  // Same behaviour as the Jobs page: "show me the latest" means jumping back to
  // page 1 as well as refetching, since a stale later page would hide tickets
  // that have just arrived. The timeout guarantees a visible spin even when the
  // refetch resolves instantly from cache.
  const handleRefresh = () => {
    setIsRefreshing(true);
    setPage(1);
    void refetch();
    window.setTimeout(() => setIsRefreshing(false), 600);
  };

  const refreshing = isRefreshing || isFetching;
  const { data: summary } = useSupportSummary();

  const tickets = useMemo(() => data?.tickets ?? [], [data]);

  const columns = useMemo<ColumnDef<AdminSupportTicket>[]>(
    () => [
      {
        accessorKey: 'subject',
        header: 'Ticket',
        cell: ({ row }) => {
          const ticket = row.original;
          return (
            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => openTicket(ticket.id)}
                  className="max-w-[320px] truncate text-left font-medium hover:underline"
                  title={ticket.subject}
                >
                  {ticket.subject}
                </button>
                {ticket.awaitingStaffReply && (
                  <span className="shrink-0 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-500">
                    needs reply
                  </span>
                )}
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {ticket.reference} · {supportCategoryLabel(ticket.category)}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: 'customer',
        header: 'Customer',
        cell: ({ row }) => (
          <div className="min-w-0 space-y-0.5">
            <p className="truncate text-sm">{row.original.customer.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {row.original.customer.email ?? '—'}
            </p>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <SupportStatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'priority',
        header: 'Priority',
        cell: ({ row }) => <SupportPriorityBadge priority={row.original.priority} />,
      },
      {
        accessorKey: 'assignedTo',
        header: 'Assignee',
        cell: ({ row }) => (
          <AssigneeCell
            ticket={row.original}
            userId={userId}
            canManage={canManage}
            employees={assignees}
          />
        ),
      },
      {
        accessorKey: 'lastMessageAt',
        header: 'Last activity',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {relative(row.original.lastMessageAt)}
          </span>
        ),
      },
    ],
    // `openTicket` closes over the router's stable `navigate`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, canManage, assignees]
  );

  const table = useReactTable({
    data: tickets,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const pagination = data?.pagination;

  return (
    <div className="space-y-6 p-4 duration-500 ease-out animate-in fade-in slide-in-from-bottom-6 sm:p-6">
      <PageHeader
        title="Support"
        description="Customer queries, oldest waiting first."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Jump to page 1 and reload the latest tickets"
          >
            <RefreshCw className={`size-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {summary && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat
            label="Needs a reply"
            value={summary.needsReply}
            tone={summary.needsReply > 0 ? 'text-amber-600 dark:text-amber-500' : undefined}
          />
          <Stat label="Unassigned" value={summary.unassigned} />
          <Stat label="Assigned to me" value={summary.assignedToMe} />
          <Stat
            label="Urgent open"
            value={summary.openByPriority.urgent}
            tone={summary.openByPriority.urgent > 0 ? 'text-destructive' : undefined}
          />
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Search by reference, subject or customer…"
            className="pl-8"
          />
        </div>

        <Select
          value={status}
          onValueChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={priority}
          onValueChange={(value) => {
            setPriority(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRIORITY_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={assignee}
          onValueChange={(value) => {
            setAssignee(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-full sm:w-[170px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ASSIGNEE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <DataTableSkeleton columns={6} />
      ) : (
        <>
          <DataTable
            table={table}
            emptyState={
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <Inbox className="size-6 text-muted-foreground" />
                <p className="text-sm font-medium">Nothing in this view</p>
                <p className="text-sm text-muted-foreground">
                  {status === 'active'
                    ? 'The queue is clear.'
                    : 'Try widening the filters above.'}
                </p>
              </div>
            }
          />

          {pagination && pagination.total > 0 && (
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Page <span className="font-medium text-foreground">{pagination.page}</span> of{' '}
                <span className="font-medium text-foreground">{pagination.totalPages}</span> ·{' '}
                <span className="font-medium text-foreground">{pagination.total}</span> tickets
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
